// Blueprint forecast — the financial model behind an initiative, pure.
//
// An initiative targets parts of the business map (processes, roles,
// systems, data stores). From the map we derive a BASELINE — what the
// targeted work costs today per year — and from the lever a default
// reduction. The customer edits any number; the model recomputes savings,
// payback and a discounted NPV. The Impact tab recomputes live as they
// type; the blueprint worker records what actually happened alongside.
//
// generate-entities.mjs copies this file to frontend/src/data/
// blueprintForecast.js — keep it dependency-free.

export const LEVERS = [
  { id: 'process', label: 'Process optimization', blurb: 'Automate the manual steps of a process as an nQube workflow; people keep the judgement calls.', reductionPct: 40 },
  { id: 'model', label: 'Train a model', blurb: 'A neural net trained on the business\'s own data in the Training Center replaces a rule of thumb or a vendor score.', reductionPct: 25 },
  { id: 'augment', label: 'Augment a role', blurb: 'An agent beside the people in a role: drafts, looks things up, prepares the decision; the person decides.', reductionPct: 30 },
  { id: 'replace', label: 'Replace a system', blurb: 'Retire a licensed system whose job an App Kit app and its workflows now do.', reductionPct: 60 },
  { id: 'newSoftware', label: 'New software', blurb: 'Software the business never had, built with App Kit to fit the process instead of the other way round.', reductionPct: 50 },
  { id: 'agent', label: 'Plug in an agent', blurb: 'An agent on the website, in the app or inside an existing system, answering and acting under review.', reductionPct: 35 },
];
export const LEVER_LABEL = Object.fromEntries(LEVERS.map((l) => [l.id, l.label]));
export const HOURS_PER_YEAR = 2080;
export const DEFAULT_HOURLY_USD = 60;

const num = (v, d = 0) => (Number.isFinite(Number(v)) ? Number(v) : d);
const round = (v) => Math.round(num(v));

export function defaultReduction(lever) {
  return LEVERS.find((l) => l.id === lever)?.reductionPct ?? 30;
}

/** Hourly rate of a role from its fully loaded annual cost. */
export function hourlyRate(role) {
  const annual = num(role?.loadedAnnualCostUsd);
  return annual > 0 ? annual / HOURS_PER_YEAR : DEFAULT_HOURLY_USD;
}

/**
 * Annual labor cost of a process, from its manual steps: minutes × volume per
 * month × 12, priced at each step's role (or the average of the roles that
 * work it, or the default rate).
 */
export function processLaborUsd(proc, rolesById = {}) {
  if (!proc) return { manualUsd: 0, totalUsd: 0, manualMinutes: 0, totalMinutes: 0 };
  const volume = num(proc.volumePerMonth) * 12;
  const workers = (Object.values(rolesById) || []).filter((r) => (r.processIds || []).includes(proc.id));
  const avg = workers.length ? workers.reduce((s, r) => s + hourlyRate(r), 0) / workers.length : DEFAULT_HOURLY_USD;
  let manualUsd = 0; let totalUsd = 0; let manualMinutes = 0; let totalMinutes = 0;
  for (const step of proc.steps || []) {
    const minutes = num(step.minutes);
    const rate = step.roleId && rolesById[step.roleId] ? hourlyRate(rolesById[step.roleId]) : avg;
    const usd = (minutes / 60) * rate * volume;
    totalUsd += usd; totalMinutes += minutes;
    if (step.manual !== false) { manualUsd += usd; manualMinutes += minutes; }
  }
  return { manualUsd: round(manualUsd), totalUsd: round(totalUsd), manualMinutes, totalMinutes };
}

/**
 * Baseline from the map for an initiative's targets.
 *   laborUsd      — manual labor in the targeted processes (every lever)
 *   licensesUsd   — yearly cost of targeted systems (replace / retire levers,
 *                   or systems whose verdict is replace / retire)
 *   headcountUsd  — loaded cost of targeted roles (augment lever: what the
 *                   agent sits beside)
 *   otherUsd      — carried from the existing forecast (free entry)
 */
export function baselineFromMap(initiative, { processes = [], roles = [], systems = [] } = {}) {
  const t = initiative?.targets || {};
  const rolesById = Object.fromEntries(roles.map((r) => [r.id, r]));
  const lever = initiative?.lever;
  let laborUsd = 0;
  for (const p of processes.filter((x) => (t.processIds || []).includes(x.id))) laborUsd += processLaborUsd(p, rolesById).manualUsd;
  let licensesUsd = 0;
  for (const s of systems.filter((x) => (t.systemIds || []).includes(x.id))) {
    if (['replace', 'newSoftware'].includes(lever) || ['replace', 'retire'].includes(s.verdict)) licensesUsd += num(s.annualCostUsd);
  }
  let headcountUsd = 0;
  if (lever === 'augment') {
    for (const r of roles.filter((x) => (t.roleIds || []).includes(x.id))) headcountUsd += num(r.headcount, 1) * num(r.loadedAnnualCostUsd);
  }
  return { laborUsd: round(laborUsd), licensesUsd: round(licensesUsd), headcountUsd: round(headcountUsd), otherUsd: round(initiative?.forecast?.baseline?.otherUsd) };
}

export function baselineTotal(baseline = {}) {
  return round(num(baseline.laborUsd) + num(baseline.licensesUsd) + num(baseline.headcountUsd) + num(baseline.otherUsd));
}

/**
 * The model. Inputs the customer owns: baseline, reductionPct, oneTimeUsd,
 * platformAnnualUsd, confidence. Outputs: annualSavingsUsd, paybackMonths,
 * npv3yUsd (horizon and rate from blueprint.* live config).
 */
export function computeForecast(forecast = {}, { discountRate = 0.08, horizonYears = 3, lever = null } = {}) {
  const baseline = { laborUsd: num(forecast.baseline?.laborUsd), licensesUsd: num(forecast.baseline?.licensesUsd), headcountUsd: num(forecast.baseline?.headcountUsd), otherUsd: num(forecast.baseline?.otherUsd) };
  const total = baselineTotal(baseline);
  const reductionPct = Math.max(0, Math.min(100, num(forecast.reductionPct, defaultReduction(lever))));
  const oneTimeUsd = Math.max(0, num(forecast.oneTimeUsd));
  const platformAnnualUsd = Math.max(0, num(forecast.platformAnnualUsd));
  const gross = total * (reductionPct / 100);
  const annualSavingsUsd = round(gross - platformAnnualUsd);
  const paybackMonths = annualSavingsUsd > 0 ? Math.round((oneTimeUsd / (annualSavingsUsd / 12)) * 10) / 10 : null;
  let npv = -oneTimeUsd;
  for (let y = 1; y <= Math.max(1, Math.round(num(horizonYears, 3))); y += 1) npv += annualSavingsUsd / ((1 + num(discountRate, 0.08)) ** y);
  return {
    baseline,
    baselineTotalUsd: total,
    reductionPct,
    oneTimeUsd,
    platformAnnualUsd,
    grossAnnualUsd: round(gross),
    annualSavingsUsd,
    paybackMonths,
    npv3yUsd: round(npv),
    horizonYears: Math.max(1, Math.round(num(horizonYears, 3))),
    confidence: ['low', 'medium', 'high'].includes(forecast.confidence) ? forecast.confidence : 'medium',
  };
}

/** Sum of what an initiative has realized so far from its monthly rows. */
export function realizedToDate(realized = {}) {
  let total = 0;
  for (const m of Object.values(realized.months || {})) total += num(m.manualUsd) + num(m.actualsUsd);
  return round(total);
}

const ACTIVE = ['accepted', 'planned', 'building', 'live', 'measuring', 'done'];

/** Portfolio rollup for the Impact tab and the org summary doc. */
export function rollup(initiatives = [], opts = {}) {
  const byLever = {}; const byStatus = {};
  let forecastAnnualUsd = 0; let oneTimeUsd = 0; let realizedUsd = 0; let suggestedAnnualUsd = 0;
  for (const it of initiatives) {
    const f = computeForecast(it.forecast || {}, { ...opts, lever: it.lever });
    byStatus[it.status] = (byStatus[it.status] || 0) + 1;
    if (it.status === 'suggested') suggestedAnnualUsd += f.annualSavingsUsd;
    if (!ACTIVE.includes(it.status)) continue;
    forecastAnnualUsd += f.annualSavingsUsd; oneTimeUsd += f.oneTimeUsd;
    const r = realizedToDate(it.realized);
    realizedUsd += r;
    const row = byLever[it.lever] || (byLever[it.lever] = { count: 0, forecastAnnualUsd: 0, realizedUsd: 0 });
    row.count += 1; row.forecastAnnualUsd += f.annualSavingsUsd; row.realizedUsd += r;
  }
  return { count: initiatives.length, active: initiatives.filter((i) => ACTIVE.includes(i.status)).length, forecastAnnualUsd: round(forecastAnnualUsd), suggestedAnnualUsd: round(suggestedAnnualUsd), oneTimeUsd: round(oneTimeUsd), realizedUsd: round(realizedUsd), byLever, byStatus };
}

/** Gaps in the map NoVA and the module point at before suggesting. */
export function mapGaps({ systems = [], dataStores = [], processes = [], units = [], roles = [] } = {}) {
  const gaps = [];
  if (!systems.length) gaps.push('No systems mapped yet — start with the ones people live in (ERP, CRM, email, spreadsheets).');
  if (!processes.length) gaps.push('No processes yet — name the three that eat the most hours.');
  if (!roles.length) gaps.push('No roles yet — who does the work, how many, roughly what they cost.');
  if (!dataStores.length) gaps.push('No data stores yet — where the records live decides what a model could learn from.');
  const noSteps = processes.filter((p) => !(p.steps || []).length);
  if (noSteps.length) gaps.push(`${noSteps.length} process${noSteps.length > 1 ? 'es have' : ' has'} no steps (${noSteps.slice(0, 3).map((p) => p.name).join(', ')}).`);
  const noOwner = processes.filter((p) => !p.ownerUnitId);
  if (noOwner.length) gaps.push(`${noOwner.length} process${noOwner.length > 1 ? 'es have' : ' has'} no owning unit.`);
  const noCost = systems.filter((s) => !num(s.annualCostUsd));
  if (systems.length && noCost.length) gaps.push(`${noCost.length} system${noCost.length > 1 ? 's have' : ' has'} no yearly cost — the replace lever cannot be priced without it.`);
  const noRate = roles.filter((r) => !num(r.loadedAnnualCostUsd));
  if (roles.length && noRate.length) gaps.push(`${noRate.length} role${noRate.length > 1 ? 's have' : ' has'} no loaded cost — minutes saved are priced at $${DEFAULT_HOURLY_USD}/h until then.`);
  const undecided = systems.filter((s) => !s.verdict || s.verdict === 'undecided');
  if (systems.length && undecided.length === systems.length) gaps.push('No system has a verdict yet (keep / augment / replace / retire).');
  return gaps;
}

/** The journey step the map's state earns ([[business-blueprint]]). */
export function blueprintStep({ systems = [], dataStores = [], processes = [], roles = [], initiatives = [] } = {}) {
  if (!systems.length) return 'systems';
  if (!dataStores.length) return 'data';
  if (!processes.length || !processes.some((p) => (p.steps || []).length)) return 'processes';
  if (!roles.length) return 'people';
  if (!initiatives.length) return 'optimize';
  if (!initiatives.some((i) => ACTIVE.includes(i.status))) return 'plan';
  if (!initiatives.some((i) => ['live', 'measuring', 'done'].includes(i.status))) return 'build';
  return 'prove';
}
